import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchNews } from '../../services/newsService.js';
import { useAppActions } from '../../context/AppContext.jsx';
import { SCREEN_HOST_ID } from '../shell/ScreenContainer.jsx';

import ScreenHeader from '../common/ScreenHeader.jsx';
import EmptyState from '../common/EmptyState.jsx';
import ErrorState from '../common/ErrorState.jsx';
import Skeleton from '../common/Skeleton.jsx';
import Icon from '../common/Icon.jsx';

import CategoryFilter from './CategoryFilter.jsx';
import NewsCard from './NewsCard.jsx';
import ArticleReader from './ArticleReader.jsx';

/**
 * NewsScreen — vertical news aggregator feed.
 *
 * Architectural notes:
 *   - Owns its own data lifecycle (fetch → loading → success/error). No
 *     news data crosses into the global context, since no other screen
 *     needs it. Keeps re-renders local.
 *   - Uses native vertical scrolling on a flex-1 child of ScreenContainer.
 *     Equivalent to React Native's <FlatList />: only the visible cards
 *     pay layout cost because the scroll container is GPU-composited and
 *     the cards use lazy image loading. For the eight-item feed today
 *     this is more than enough; if the feed grows past ~50 items consider
 *     virtualisation (react-window).
 *   - AbortController on fetch — if the user switches tabs mid-flight we
 *     don't want a stale setState landing after unmount.
 *   - The category filter sits between the header and the list, NOT inside
 *     the sticky header. Sticking the filter feels cluttered on small
 *     screens — confirmed by the UX team on the v0.4 design review.
 */

export default function NewsScreen() {
  const [category, setCategory] = useState('all');
  const [state, setState] = useState({ status: 'loading', data: [], error: null });
  const [openArticle, setOpenArticle] = useState(null);
  const { seedOraclePrompt } = useAppActions();

  /**
   * Data loader — abstracted so the retry button can reuse it. The function
   * is intentionally not memoised; useEffect's dep array handles that.
   */
const load = useCallback(async (cat, signal) => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const { articles } = await fetchNews({ category: cat, signal });
      setState({ status: 'success', data: articles, error: null });
    } catch (err) {
      if (err.name === 'AbortError') return;
      setState({ status: 'error', data: [], error: err });
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    load(category, ctrl.signal);
    return () => ctrl.abort();
  }, [category, load]);

  /**
   * Tapping a card opens the article in the reader. It used to jump straight
   * to the Oracle with a "more context on this headline" prompt, which meant
   * the one thing you could not do with a news story was read it. The Oracle
   * hand-off now lives at the foot of the reader.
   */
  /**
   * Reader open/close is tied to a history entry so the Android hardware back
   * button closes the reader instead of exiting the app — MainActivity routes
   * back presses to webView.goBack(), and with no entry to pop that call does
   * nothing and the activity finishes.
   *
   * Two things make this fiddlier than it looks, both learned the hard way:
   *
   *   pushState lives in the click handler, NOT in an effect. Pushing on mount
   *   and popping in the cleanup breaks under StrictMode — the dev-only
   *   mount → unmount → mount cycle fires history.back() between the two
   *   mounts, and the resulting popstate closes the reader the instant it
   *   opens. Event handlers run once.
   *
   *   `ownsEntry` is flipped synchronously BEFORE calling back(). history.back()
   *   resolves asynchronously, so reading history.state to decide whether an
   *   entry is still ours reports the stale value to anything that runs in the
   *   same tick — which is how "Ask the Oracle" (close, then switch tab, then
   *   unmount) managed to pop twice and navigate out of the app entirely.
   */
  const ownsEntry = useRef(false);

  const releaseEntry = useCallback(() => {
    if (!ownsEntry.current) return;
    ownsEntry.current = false;
    window.history.back();
  }, []);

  const handleOpen = useCallback((article) => {
    window.history.pushState({ f1Reader: true }, '');
    ownsEntry.current = true;
    setOpenArticle(article);
  }, []);

  const handleCloseReader = useCallback(() => {
    setOpenArticle(null);
    releaseEntry();
  }, [releaseEntry]);

  useEffect(() => {
    if (!openArticle) return undefined;
    // A back press consumes the entry for us; just reflect it in state.
    const onPop = () => {
      ownsEntry.current = false;
      setOpenArticle(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [openArticle]);

  // Leaving the tab with the reader open would otherwise strand the entry,
  // costing the user a dead back press later.
  useEffect(() => () => releaseEntry(), [releaseEntry]);

  const handleAskOracle = useCallback(
    (article) => {
      handleCloseReader();
      seedOraclePrompt(`Give me more context on this headline: "${article.title}"`);
    },
    [handleCloseReader, seedOraclePrompt]
  );

  // The reader covers the screen area, so it mounts outside this screen's
  // scroll container — see SCREEN_HOST_ID in ScreenContainer.
  const screenHost = typeof document !== 'undefined' ? document.getElementById(SCREEN_HOST_ID) : null;

  return (
    <div className="flex flex-col px-5 pb-8">
      <ScreenHeader
        eyebrow="LIVE FEED"
        title="Latest News"
        subtitle="Curated by Formula One Management"
        trailing={
          <button
            className="pressable flex h-9 w-9 items-center justify-center rounded-full bg-bg-card2 text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
            onClick={() => load(category, new AbortController().signal)}
            aria-label="Refresh"
          >
            <Icon name="refresh" size={16} />
          </button>
        }
      />

      <CategoryFilter value={category} onChange={setCategory} />

      {state.status === 'loading' ? <NewsFeedSkeleton /> : null}

      {state.status === 'error' ? (
        <ErrorState
          title="Feed unavailable"
          message="The FOM news feed didn't respond in time. Pull to retry."
          onRetry={() => load(category, new AbortController().signal)}
        />
      ) : null}

      {state.status === 'success' && state.data.length === 0 ? (
        <EmptyState
          title="No stories in this category"
          message="Try a different filter — breaking news will surface here as soon as it lands."
          icon="filter"
        />
      ) : null}

      {state.status === 'success' && state.data.length > 0 ? (
        <NewsList articles={state.data} onOpen={handleOpen} />
      ) : null}

      {openArticle && screenHost
        ? createPortal(
            <ArticleReader
              key={openArticle.id}
              article={openArticle}
              onClose={handleCloseReader}
              onAskOracle={handleAskOracle}
            />,
            screenHost
          )
        : null}
    </div>
  );
}

/**
 * NewsList — pure presentation. Splits the first item into hero variant.
 * Production: swap to a virtualised list (react-window FixedSizeList with
 * variable measurement, or @tanstack/react-virtual) once feed length grows.
 */
function NewsList({ articles, onOpen }) {
  // The hero is a full-bleed image card, but two thirds of syndicated articles
  // carry no cover image — so blindly promoting articles[0] frequently rendered
  // an empty gradient box as the most prominent element on the screen. Prefer
  // the freshest article that actually has an image, staying near the top of
  // the feed so we don't promote something stale.
  const heroIndex = (() => {
    const limit = Math.min(articles.length, 12);
    for (let i = 0; i < limit; i++) {
      if (articles[i].coverImage) return i;
    }
    return 0; // nothing in the window has art — fall back to the freshest
  })();

  const hero = articles[heroIndex];
  const rest = articles.filter((_, i) => i !== heroIndex);

  return (
    <div className="animate-fade-in">
      <NewsCard article={hero} variant="hero" onOpen={onOpen} />
      <div className="mb-2 flex items-center gap-2 px-1 text-2xs font-semibold uppercase tracking-widest-plus text-ink-tertiary">
        <span className="h-px flex-1 bg-stroke" />
        <span>More stories</span>
        <span className="h-px flex-1 bg-stroke" />
      </div>
      {rest.map((article) => (
        <NewsCard key={article.id} article={article} variant="compact" onOpen={onOpen} />
      ))}
    </div>
  );
}

function NewsFeedSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      <Skeleton className="h-72 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-24 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-24 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-24 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-24 w-full" rounded="rounded-2xl" />
    </div>
  );
}
